import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { TranslationService } from '@/layout/service/translation.service';
import { MetricChartComponent, MetricSeries } from '@/shared/metric-chart/metric-chart.component';
import { DonutSegment, MetricDonutComponent } from '@/shared/metric-chart/metric-donut.component';
import { MetricTileComponent } from '@/shared/ui/metric-tile/metric-tile.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { CloudMetricsService } from '../cloud-metrics.service';
import { InsightContext, InsightRange, InsightSummaryStore, insightRangeLabel } from '../insight-panel';
import { MetricPoint, MetricQueryResult, MetricValues } from '../metrics.models';
import { FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { describeWorkingHours, weekdayNames } from '@/cloud/models/working-hours.models';

/** Range KPIs derived from the occupancy metric's summary values (seconds are totals over the range). */
export interface OccupancyKpis {
    knownHours: number | null;
    /** Occupied share of the time the occupancy was known, 0-100. */
    utilisationPct: number | null;
    /** Booked share of the known time, 0-100. */
    bookedPct: number | null;
    /** Booked but nobody there (no-shows, meetings ending early). */
    bookedVacantHours: number | null;
    /** Booked-but-empty share of the booked time, 0-100. */
    bookedVacantPctOfBooked: number | null;
    /** Occupied without a booking (ad-hoc use). */
    adHocHours: number | null;
    avgPeople: number | null;
    peakPeople: number | null;
    capacity: number | null;
    /** Peak people as a share of capacity, 0-100. */
    peakOfCapacityPct: number | null;
}

const num = (values: MetricValues | null | undefined, key: string): number | null => {
    const v = values?.[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
};
const pct = (part: number | null, whole: number | null): number | null => (part != null && whole != null && whole > 0 ? (part / whole) * 100 : null);
const hours = (seconds: number | null): number | null => (seconds == null ? null : seconds / 3600);

export function occupancyKpis(values: MetricValues | null | undefined): OccupancyKpis {
    const known = num(values, 'knownSeconds');
    const occupied = num(values, 'occupiedSeconds');
    const booked = num(values, 'bookedSeconds');
    const bookedVacant = num(values, 'bookedVacantSeconds');
    const peak = num(values, 'peakPeople');
    const capacity = num(values, 'capacity');
    return {
        knownHours: hours(known),
        utilisationPct: pct(occupied, known),
        bookedPct: pct(booked, known),
        bookedVacantHours: hours(bookedVacant),
        bookedVacantPctOfBooked: pct(bookedVacant, booked),
        adHocHours: hours(num(values, 'occupiedUnbookedSeconds')),
        avgPeople: num(values, 'avgPeople'),
        peakPeople: peak,
        capacity,
        peakOfCapacityPct: pct(peak, capacity)
    };
}

export interface OccupancySeriesLabels {
    bookedUsed: string;
    adHoc: string;
    bookedVacant: string;
    occupied: string;
}

export interface OccupancyBreakdownLabels extends OccupancySeriesLabels {
    free: string;
    vacant: string;
}

/**
 * How the known time over the range split up, in hours: booked and used, used without a booking,
 * booked but empty and free (the neutral rest). These are disjoint and sum to the known time, so
 * they read as one part-to-whole donut. Without a calendar, just occupied and vacant.
 */
export function occupancyBreakdown(values: MetricValues | null | undefined, withBookings: boolean, labels: OccupancyBreakdownLabels): DonutSegment[] {
    const known = num(values, 'knownSeconds');
    const occupied = num(values, 'occupiedSeconds');
    if (known == null || known <= 0 || occupied == null) return [];
    const h = (seconds: number) => Math.max(0, seconds) / 3600;
    if (!withBookings) {
        return [
            { key: 'occupied', label: labels.occupied, value: h(occupied), slot: 0 },
            { key: 'vacant', label: labels.vacant, value: h(known - occupied), slot: null }
        ];
    }
    const adHoc = Math.min(occupied, num(values, 'occupiedUnbookedSeconds') ?? 0);
    const bookedVacant = num(values, 'bookedVacantSeconds') ?? 0;
    return [
        { key: 'bookedUsed', label: labels.bookedUsed, value: h(occupied - adHoc), slot: 0 },
        { key: 'adHoc', label: labels.adHoc, value: h(adHoc), slot: 1 },
        { key: 'bookedVacant', label: labels.bookedVacant, value: h(bookedVacant), slot: 2 },
        { key: 'free', label: labels.free, value: h(known - occupied - bookedVacant), slot: null }
    ];
}

/**
 * Interval length for the timeline columns: hours over a day, three-hour blocks over a week and
 * days over a month. The 5-minute default reads as 0 %/100 % needles instead of a pattern of use.
 */
export function occupancyBucketMinutes(rangeHours: number): number {
    return rangeHours <= 24 ? 60 : rangeHours <= 24 * 7 ? 180 : 1440;
}

/**
 * Share of each interval (0-100 % of the known time) the room was booked and used, used without
 * a booking, and booked but empty - these are disjoint, so they stack. Without a calendar, only
 * the occupied share.
 */
export function occupancyUsageSeries(points: MetricPoint[], withBookings: boolean, labels: OccupancySeriesLabels): MetricSeries[] {
    const share = (value: (v: MetricValues) => number | null): MetricSeries['points'] =>
        points.flatMap((p) => {
            const v = value(p.values);
            const s = pct(v, num(p.values, 'knownSeconds'));
            return s == null ? [] : [{ t: Date.parse(p.t), v: Math.round(s * 10) / 10 }];
        });
    if (!withBookings) return [{ key: 'occupied', label: labels.occupied, points: share((v) => num(v, 'occupiedSeconds')) }];
    return [
        {
            key: 'bookedUsed',
            label: labels.bookedUsed,
            points: share((v) => {
                const occupied = num(v, 'occupiedSeconds');
                return occupied == null ? null : Math.max(0, occupied - (num(v, 'occupiedUnbookedSeconds') ?? 0));
            })
        },
        { key: 'adHoc', label: labels.adHoc, points: share((v) => num(v, 'occupiedUnbookedSeconds')) },
        { key: 'bookedVacant', label: labels.bookedVacant, points: share((v) => num(v, 'bookedVacantSeconds')) }
    ];
}

/** Average and peak people per interval (only intervals that report counts). */
export function peopleSeries(points: MetricPoint[], labels: { avg: string; peak: string }): MetricSeries[] {
    const series = (key: string, label: string): MetricSeries => ({
        key,
        label,
        points: points.flatMap((p) => {
            const v = num(p.values, key);
            return v == null ? [] : [{ t: Date.parse(p.t), v }];
        })
    });
    return [series('avgPeople', labels.avg), series('peakPeople', labels.peak)].filter((s) => s.points.length > 0);
}

/** The room's (single) occupancy source: the combined series, or the first source's. */
function roomPoints(result: MetricQueryResult | null): MetricPoint[] {
    if (!result) return [];
    return result.combined?.length ? result.combined : (result.sources?.[0]?.points ?? []);
}

function roomSummary(result: MetricQueryResult | null): MetricValues | null {
    if (!result) return null;
    return result.summary && Object.keys(result.summary).length ? result.summary : (result.sources?.[0]?.summary ?? null);
}

/**
 * Room occupancy and bookings over the selected range: utilisation, booked share, no-shows,
 * ad-hoc use and people counts, a donut of how the time was spent, stacked columns of how each
 * interval was used and a people-count column chart. Registered as the "occupancy" insight panel; publishes utilisation to the summary strip.
 */
@Component({
    selector: 'app-occupancy-insight-panel',
    standalone: true,
    imports: [FormsModule, SelectButtonModule, MetricChartComponent, MetricDonutComponent, MetricTileComponent, LoadingSkeletonComponent, EmptyStateComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (loading() && !result()) {
            <app-loading-skeleton preset="tiles" [count]="4" [label]="t()('occupancy.panelTitle')" />
        } @else if (error()) {
            <app-empty-state icon="fa fa-circle-exclamation" tone="error" [compact]="true" [title]="t()('occupancy.loadFailed')" [actionLabel]="t()('insights.retry')" actionIcon="pi pi-refresh" (action)="retry()" />
        } @else {
            @if (workingHoursNote(); as note) {
                <div class="hours-scope">
                    <span class="hours-scope__note"><i class="fa fa-clock" aria-hidden="true"></i> {{ note }}</span>
                    <p-selectbutton [options]="scopeOptions()" [ngModel]="allHours()" (ngModelChange)="allHours.set($event)" optionLabel="label" optionValue="value" [allowEmpty]="false" size="small" [ariaLabel]="t()('occupancy.scopeLabel')" />
                </div>
            }
            <div class="kpi-row">
                <app-metric-tile class="kpi-utilisation" [label]="t()('occupancy.utilisation')" [value]="kpis().utilisationPct" unit="%" icon="fa fa-chart-pie" format="1.0-0" [caption]="t()('occupancy.utilisationCaption')" />
                @if (withBookings()) {
                    <app-metric-tile class="kpi-booked" [label]="t()('occupancy.booked')" [value]="kpis().bookedPct" unit="%" icon="fa fa-calendar-check" format="1.0-0" [caption]="t()('occupancy.bookedCaption')" />
                    <app-metric-tile class="kpi-no-shows" [label]="t()('occupancy.noShows')" [value]="kpis().bookedVacantHours" [unit]="t()('occupancy.hours')" icon="fa fa-calendar-xmark" format="1.0-1" [caption]="noShowCaption()" />
                    <app-metric-tile class="kpi-ad-hoc" [label]="t()('occupancy.adHoc')" [value]="kpis().adHocHours" [unit]="t()('occupancy.hours')" icon="fa fa-user-clock" format="1.0-1" [caption]="t()('occupancy.adHocCaption')" />
                }
                @if (withPeople()) {
                    <app-metric-tile class="kpi-peak" [label]="t()('occupancy.peakPeople')" [value]="kpis().peakPeople" icon="fa fa-users" format="1.0-0" [caption]="peakCaption()" />
                    <app-metric-tile class="kpi-avg" [label]="t()('occupancy.avgPeople')" [value]="kpis().avgPeople" icon="fa fa-user-group" format="1.0-1" [caption]="t()('occupancy.whenOccupied')" />
                }
            </div>
            <div class="charts">
                <div class="chart chart--breakdown">
                    <h3 class="chart-title">{{ t()('occupancy.breakdownChart') }}</h3>
                    <app-metric-donut
                        [segments]="breakdown()"
                        [unit]="t()('occupancy.hours')"
                        [centerValue]="kpis().utilisationPct"
                        [centerLabel]="t()('occupancy.donutCenter')"
                        [ariaLabel]="breakdownAria()"
                        [emptyText]="t()('occupancy.historyPending')"
                    />
                </div>
                <div class="chart chart--usage">
                    <h3 class="chart-title">{{ t()('occupancy.usageChart') }}</h3>
                    <app-metric-chart
                        type="bar"
                        [series]="usageChart()"
                        unit="%"
                        [stacked]="true"
                        [yMax]="100"
                        [bucketMinutes]="bucketMinutes()"
                        [windowHours]="range().hours"
                        [ariaLabel]="t()('occupancy.usageChart')"
                        [emptyText]="t()('occupancy.historyPending')"
                    />
                </div>
                @if (peopleChart().length) {
                    <div class="chart chart--people">
                        <h3 class="chart-title">{{ t()('occupancy.peopleChart') }}</h3>
                        <app-metric-chart
                            type="bar"
                            [series]="peopleChart()"
                            [unit]="t()('occupancy.people')"
                            [bucketMinutes]="bucketMinutes()"
                            [windowHours]="range().hours"
                            height="clamp(12rem, 20vw, 16rem)"
                            [ariaLabel]="t()('occupancy.peopleChart')"
                            [emptyText]="t()('occupancy.historyPending')"
                        />
                    </div>
                }
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-4);
                min-width: 0;
                container: occupancy / inline-size;
            }
            .hours-scope {
                display: flex;
                flex-wrap: wrap;
                gap: var(--app-space-2) var(--app-space-3);
                align-items: center;
                justify-content: space-between;
            }
            .hours-scope__note {
                font-size: 0.875rem;
                color: var(--text-color-secondary);
                display: inline-flex;
                gap: 0.5rem;
                align-items: center;
            }
            .kpi-row {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(min(100%, 10rem), 1fr));
                gap: var(--app-space-3);
            }
            .charts {
                display: grid;
                grid-template-columns: minmax(0, 1fr);
                gap: var(--app-space-4);
            }
            @container occupancy (min-width: 52rem) {
                .charts {
                    grid-template-columns: minmax(18rem, 1fr) minmax(0, 2fr);
                }
                .chart--people {
                    grid-column: 1 / -1;
                }
            }
            .chart--breakdown app-metric-donut {
                flex: 1;
            }
            .chart {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-2);
                min-width: 0;
            }
            .chart-title {
                margin: 0;
                font-size: 0.875rem;
                font-weight: 600;
                color: var(--text-color-secondary);
            }
        `
    ]
})
export class OccupancyInsightPanelComponent {
    private metrics = inject(CloudMetricsService);
    private destroyRef = inject(DestroyRef);
    private summaryStore = inject(InsightSummaryStore, { optional: true });
    private translation = inject(TranslationService);
    readonly t = this.translation.t;

    readonly context = input.required<InsightContext>();
    readonly range = input.required<InsightRange>();

    readonly result = signal<MetricQueryResult | null>(null);
    readonly loading = signal(true);
    readonly error = signal(false);

    /** Only the room identity: the context also changes on every live usage update, which must not refetch. */
    private readonly locationId = computed(() => this.context().id);
    private readonly hasBookingNow = computed(() => !!this.context().location?.usage?.hasBooking);
    private request = 0;

    readonly summary = computed(() => roomSummary(this.result()));
    readonly kpis = computed(() => occupancyKpis(this.summary()));
    readonly withBookings = computed(() => this.hasBookingNow() || this.kpis().bookedPct != null);
    readonly withPeople = computed(() => this.kpis().peakPeople != null || this.kpis().avgPeople != null);
    readonly rangeLabel = computed(() => insightRangeLabel(this.range().hours, this.t()));

    readonly noShowCaption = computed(() => {
        const share = this.kpis().bookedVacantPctOfBooked;
        return share == null ? null : this.t()('occupancy.noShowsCaption').replace('{pct}', `${Math.round(share)} %`);
    });
    readonly peakCaption = computed(() => {
        const { peakOfCapacityPct, capacity } = this.kpis();
        return peakOfCapacityPct == null || capacity == null
            ? null
            : this.t()('occupancy.peakCaption')
                  .replace('{pct}', `${Math.round(peakOfCapacityPct)} %`)
                  .replace('{cap}', String(capacity));
    });

    readonly bucketMinutes = computed(() => occupancyBucketMinutes(this.range().hours));

    readonly breakdown = computed(() =>
        occupancyBreakdown(this.summary(), this.withBookings(), {
            bookedUsed: this.t()('occupancy.series.bookedUsed'),
            adHoc: this.t()('occupancy.series.adHoc'),
            bookedVacant: this.t()('occupancy.series.bookedVacant'),
            occupied: this.t()('occupancy.series.occupied'),
            free: this.t()('occupancy.series.free'),
            vacant: this.t()('occupancy.series.vacant')
        })
    );
    readonly breakdownAria = computed(() => {
        const total = this.breakdown().reduce((sum, s) => sum + s.value, 0);
        const parts = this.breakdown().map((s) => `${s.label} ${Math.round((s.value / total) * 100)} %`);
        return `${this.t()('occupancy.breakdownChart')}: ${parts.join(', ')}`;
    });

    readonly usageChart = computed(() =>
        occupancyUsageSeries(roomPoints(this.result()), this.withBookings(), {
            bookedUsed: this.t()('occupancy.series.bookedUsed'),
            adHoc: this.t()('occupancy.series.adHoc'),
            bookedVacant: this.t()('occupancy.series.bookedVacant'),
            occupied: this.t()('occupancy.series.occupied')
        })
    );
    readonly peopleChart = computed(() => peopleSeries(roomPoints(this.result()), { avg: this.t()('occupancy.series.avgPeople'), peak: this.t()('occupancy.series.peakPeople') }));

    constructor() {
        effect(() => {
            const locationId = this.locationId();
            this.allHours();
            const range = this.range();
            untracked(() => void this.load(locationId, range));
        });

        // Utilisation over the range in the page's summary strip.
        effect(() => {
            const utilisation = this.kpis().utilisationPct;
            const label = this.t()('insights.utilisation');
            const caption = this.rangeLabel();
            untracked(() => this.summaryStore?.set('occupancy', utilisation == null ? [] : [{ key: 'utilisation', label, value: utilisation, unit: '%', icon: 'fa fa-chart-pie', format: '1.0-0', caption }]));
        });
        this.destroyRef.onDestroy(() => this.summaryStore?.clear('occupancy'));
    }

    /** Count every hour instead of only the tenant's working hours. */
    readonly allHours = signal(false);
    readonly scopeOptions = computed(() => [
        { label: this.t()('occupancy.scope.working'), value: false },
        { label: this.t()('occupancy.scope.all'), value: true }
    ]);
    /** What the numbers cover, when the tenant has working hours; null (no note or switch) otherwise. */
    readonly workingHoursNote = computed(() => {
        const hours = this.result()?.workingHours;
        if (!hours) return null;
        const text = describeWorkingHours(hours.days, weekdayNames(this.translation.currentLanguage()));
        const note = this.t()(hours.applied ? 'occupancy.workingHoursNote' : 'occupancy.allHoursNote')
            .replace('{hours}', text)
            .replace('{zone}', hours.timeZone);
        // Name the location(s) whose override applies, so a room in another office explains its own hours.
        const names = [...new Set([hours.hoursFrom, hours.timeZoneFrom].filter((s) => s?.kind === 'location' && s.name).map((s) => s!.name!))];
        return names.length ? `${note} · ${this.t()('occupancy.setOn').replace('{name}', names.join(', '))}` : note;
    });

    retry(): void {
        void this.load(this.locationId(), this.range());
    }

    private async load(locationId: string, range: InsightRange): Promise<void> {
        const request = ++this.request;
        this.loading.set(true);
        this.error.set(false);
        try {
            const result = await this.metrics.getLocationMetrics(locationId, { type: 'occupancy', from: range.from, to: range.to, bucketMinutes: occupancyBucketMinutes(range.hours), includeBreakdown: false, workingHours: !this.allHours() });
            if (request === this.request) this.result.set(result);
        } catch {
            if (request !== this.request) return;
            this.result.set(null);
            this.error.set(true);
        } finally {
            if (request === this.request) this.loading.set(false);
        }
    }
}

/** Occupancy panel is shown for rooms that report usage (occupancy sensors or a calendar). */
export function roomHasUsage(context: InsightContext): boolean {
    return !!context.location?.usage;
}
